﻿import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TextField,
  IconButton,
  Container,
  Box,
  Autocomplete,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import { useDispatch, useSelector } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import axios from "@/features/axiosHelper";
import {
  getAllCustomFields,
  getAllConnections,
  getAllCustomFieldsAndValues,
} from "@/features/maintenance/maintenanceSlice";

const AddEditCustomFieldValueForConnection = ({
  open,
  onClose,
  onSuccess,
  recordToEdit,
}) => {
  const dispatch = useDispatch();

  const allCustomFields = useSelector(getAllCustomFields);
  const allConnections = useSelector(getAllConnections);
  const allCustomFieldsAndValues = useSelector(getAllCustomFieldsAndValues);

  const isEditing = Boolean(recordToEdit);

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedFieldCode, setSelectedFieldCode] = useState(null);
  const [fieldCodeValue, setFieldCodeValue] = useState("");

  useEffect(() => {
    if (open) {
      if (isEditing) {
        const fieldToEdit = allCustomFields.find(
          (f) => f?.ConnectionCustomFieldId === recordToEdit?.FieldId,
        );

        setSelectedFieldCode(fieldToEdit);
        setFieldCodeValue(recordToEdit.FieldValue || "");

        // Find the Connection
        const connectionToEdit = allConnections.find(
          (c) => c?.ConnectionId === recordToEdit?.ConnectionId,
        );
        setSelectedConnection(connectionToEdit);
      } else {
        // Add
        setSelectedConnection(null);
        setSelectedFieldCode(null);
        setFieldCodeValue("");
      }
    }
  }, [open, isEditing, recordToEdit, allCustomFields, allConnections]);

  const availableFields = isEditing
    ? allCustomFields.filter(
        (f) => f?.ConnectionCustomFieldId === recordToEdit.FieldId,
      )
    : allCustomFields.filter((field) => {
        if (!selectedConnection) return true;

        const isAssigned = allCustomFieldsAndValues.some(
          (assigned) =>
            assigned.FieldId === field?.ConnectionCustomFieldId &&
            assigned?.ConnectionId === selectedConnection?.ConnectionId,
        );

        return !isAssigned;
      });

  const getNoOptionsText = () => {
    if (!allCustomFields || allCustomFields.length === 0) {
      return "No custom fields configured.";
    }
    return "All available fields have been assigned to this connection.";
  };

  const handleSave = () => {
    const payload = {
      FieldId: selectedFieldCode?.ConnectionCustomFieldId,
      FieldValue: fieldCodeValue,
    };

    const connectionId = selectedConnection?.ConnectionId;

    axios
      .post(`/connectioncustomfield/custom-field-value`, payload, {
        params: { connectionId: connectionId },
      })
      .then((response) => {
        console.log(response.data);
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: response.data,
          }),
        );
        if (onSuccess) {
          onSuccess();
          setSelectedFieldCode(null);
          setFieldCodeValue("");
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  const handleCancel = () => {
    setSelectedFieldCode(null);
    setFieldCodeValue("");
    onClose();
  };

  return (
    <Container>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth={"sm"}
        fullWidth={true}
        PaperProps={{ style: { borderRadius: 15 } }}
      >
        <DialogTitle
          className="add-crossref-dialog-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
            padding: "16px 24px",
          }}
        >
          <div style={{ fontWeight: "var(--font-semi-bold)" }}>
            {isEditing ? "Edit" : "Add"} Field Value
          </div>
          <IconButton
            className="closeIconButton"
            style={{ color: "var(--default-light)" }}
            onClick={handleCancel}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className="dialogContent"
          style={{
            backgroundColor: "var(--disabled)",
            padding: "35px",
            minWidth: 400,
          }}
        >
          <Box
            sx={{
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="contentAccent3"
                    className="endpoint-section-header"
                  >
                    Connection
                  </Typography>
                  <Autocomplete
                    id="connection-dropdown-popup"
                    sx={{ width: "300px" }}
                    options={allConnections}
                    value={selectedConnection}
                    disabled={isEditing} // <--- Locks the dropdown if editing!
                    onChange={(event, newValue) => {
                      setSelectedConnection(newValue);
                      setSelectedFieldCode(null); // Reset field code if connection changes
                    }}
                    getOptionLabel={(option) => option.ConnectionName || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.ConnectionId === value?.ConnectionId
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Connection"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="contentAccent3"
                    className="endpoint-section-header"
                  >
                    Field Code
                  </Typography>
                  <Autocomplete
                    id="field-code-dropdown"
                    sx={{ width: "300px" }}
                    options={availableFields}
                    value={selectedFieldCode}
                    disabled={isEditing} // <--- Locks the dropdown if editing!
                    onChange={(event, newValue) =>
                      setSelectedFieldCode(newValue)
                    }
                    getOptionLabel={(option) => option.CustomFieldCode || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.ConnectionCustomFieldId ===
                      value?.ConnectionCustomFieldId
                    }
                    noOptionsText={getNoOptionsText()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a Field Code"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="contentAccent3"
                    className="endpoint-section-header"
                  >
                    Field Value
                  </Typography>
                  <TextField
                    id="connection-value-input"
                    sx={{ width: "300px" }}
                    placeholder="Enter Value"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={fieldCodeValue}
                    onChange={(e) => setFieldCodeValue(e.target.value)}
                    disabled={!selectedFieldCode} // Lock the textbox until a field is chosen!
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <ShepButton variant="secondary" onClick={handleCancel}>
            Cancel
          </ShepButton>
          <ShepButton onClick={handleSave}>
            {isEditing ? "Update" : "Add"}
          </ShepButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddEditCustomFieldValueForConnection;
